/**
 * Backbone Collection to support GraphQL technology.
 * GraphQL is based on filter/paging/sorting in the server, so every change of one of those triggering a request to the server with a generated query
 * In order to avoid case of saving some meta data that server doesn't return an answer for, we save also "last success meta data".
 * In GraphQL we are responsible in the client for the request and also what we are expecting to get in the response.
 *
 * Usage:
 *  - In order to use this GraphQL you should add "entityName" attribute in the resource of the collection. This entity name is the entity we ask for in the GraphQL query
 *  - Some of the filters might need to be ignored. In this case you need to add it as "internalFilters" attribute on the collection instance.
 *  - To tell the fetch query what you want to get in the response you can set "responseParams" attribute on the collection or hang it on the 'options' for the fetch. responseParams is an array of attribute names.
 *
 */
define(function (require) {
    "use strict";

    var _ = require("underscore");
    var RevisionedCollection = require("collections/RevisionedCollection");

    var ORDER_DIRECTION = {
        ASC: "asc",
        DESC: "desc"
    };

    var META_DATA = {
        totalItems: 0,
        page: 1,
        pageSize: 25,
        orderBy: [],
        orderDirection: [],
        filters: {}
    };

    var ENUMS = ["orderDirection"]; // we are ignoring stringify if the value is a value of enum. the keys of what is enum declared in ENUMS constant.

    var TOTAL_ITEMS_HEADER = "X-Total-Count";


    var GraphQLCollection = RevisionedCollection.extend({
        ORDER_DIRECTION: ORDER_DIRECTION,
        initialize: function(models, options){
            options = options || {} ;
            models = models || [];

            this.fetchOnChange = _.has(options, "fetchOnChange") ? options.fetchOnChange : true; // fetch on meta data change automatically or not.
            this.metaData = _.cloneDeep(META_DATA);
            this.lastSuccessMetaData = _.cloneDeep(META_DATA);

            RevisionedCollection.prototype.initialize.call(this, models, options);
        },

        getTotalPages: function() {
            return Math.ceil(this.getTotalItems() / this.getPageSize());
        },

        getTotalItems: function() {
            return this.lastSuccessMetaData.totalItems;
        },

        getPage: function() {
            return this.lastSuccessMetaData.page;
        },

        setPage: function(page, options) {
            options = options || {};
            var fetchOnChange = _.has(options, "fetchOnChange") ? options.fetchOnChange : this.fetchOnChange;

            this.metaData.page = page;

            if (fetchOnChange) {
                this.fetch();
            }
        },

        getPageSize: function() {
            return this.lastSuccessMetaData.pageSize;
        },

        getFilterValue: function(filterName) {
            return this.lastSuccessMetaData.filters[filterName];
        },

        setFilterValue: function(filterName, filterValue, options) {
            options = options || {};
            var fetchOnChange = _.has(options, "fetchOnChange") ? options.fetchOnChange : this.fetchOnChange;

            this.metaData.filters[filterName] = filterValue;

            // when filter is changed we need to reset the page number, unless we've told not to
            if (!options.ignoreResetPage) {
                this.setPage(1, {fetchOnChange:false});
            }

            if (fetchOnChange) {
                this.fetch();
            }
        },

        getFilters: function() {
            return this.lastSuccessMetaData.filters;
        },

        setFilters: function(filters, options) {
            options = options || {};
            var fetchOnChange = _.has(options, "fetchOnChange") ? options.fetchOnChange : this.fetchOnChange;

            this.metaData.filters = filters;

            if (fetchOnChange) {
                this.fetch();
            }
        },

        getOrderBy: function() {
            return this.lastSuccessMetaData.orderBy;
        },

        setOrderBy: function(orderBy, options) {
            options = options || {};
            var fetchOnChange = _.has(options, "fetchOnChange") ? options.fetchOnChange : this.fetchOnChange;

            this.metaData.orderBy = _.isArray(orderBy) ? orderBy : [orderBy];

            if (fetchOnChange) {
                this.fetch();
            }
        },

        getOrderDirection: function() {
            return this.lastSuccessMetaData.orderDirection;
        },

        setOrderDirection: function(orderDirection, options) {
            options = options || {};
            var fetchOnChange = _.has(options, "fetchOnChange") ? options.fetchOnChange : this.fetchOnChange;

            this.metaData.orderDirection = _.isArray(orderDirection) ? orderDirection : [orderDirection];

            if (fetchOnChange) {
                this.fetch();
            }
        },

        /**
         * This method generates the request and response queries for graphQL query.
         * This method called with different arguments for request and response.
         * Basically it stringify in graphQL way, but we ignore this stringify if the value is a value of enum. the keys of what is enum declared in ENUMS constant.
         * @param val
         * @param gqlChars
         * @param ignoreStringify
         * @returns {*}
         * @private
         */
        _stringifyValueByGraphQL: function(val, gqlChars, ignoreStringify) {
            gqlChars = gqlChars || {};
            var objWrapperLeft = _.has(gqlChars, "objWrapperLeft") ? gqlChars.objWrapperLeft : "{";
            var objWrapperRight = _.has(gqlChars, "objWrapperRight") ? gqlChars.objWrapperRight : "}";
            var objKeyValueSeparator = _.has(gqlChars, "objKeyValueSeparator") ? gqlChars.objKeyValueSeparator : ":";

            if (_.isBoolean(val) || _.isNumber(val)) {
                return val;
            }
            else if (_.isString(val)) {
                if (ignoreStringify) {
                    return val;
                }
                else {
                    return JSON.stringify(val);
                }
            }
            else if (_.isArray(val)) {
                var arrStr = _.map(val, _.bind(function(item) {
                    return this._stringifyValueByGraphQL(item, {}, ignoreStringify);
                }, this));
                return "[" + arrStr + "]";
            }
            else if (_.isObject(val)) {
                var objStr = _.map(_.keys(val), _.bind(function(key){
                    var strVal = this._stringifyValueByGraphQL(val[key], {}, _.contains(ENUMS, key));
                    if (_.isNull(strVal) || _.isUndefined(strVal)) {
                        return key;
                    }
                    else {
                        return key+objKeyValueSeparator+strVal;
                    }
                }, this)).join(" ");
                return objWrapperLeft + objStr + objWrapperRight;
            }
        },

        _prepareRequestObj: function(metaData) {
            var requestObj = {
                first: metaData.pageSize,
                offset: (metaData.page - 1) * metaData.pageSize
            };
            if (!_.isEmpty(metaData.orderBy)) {
                requestObj.orderBy = metaData.orderBy;
                requestObj.orderDirection = metaData.orderDirection;
            }

            var filters = {};
            _.each(metaData.filters, _.bind(function(val, key) {
                if (_.isNull(val) || _.isUndefined(val) || (_.isArray(val) && _.isEmpty(val)) || this._isFilterIgnored(key)) {
                    // DO NOTHING
                }
                else {
                    filters[key] = val;
                }
            }, this));
            requestObj = $.extend({}, requestObj, filters);

            return requestObj;
        },

        _prepareResponseParams: function(responseParams) {
            var responseObj = {};
            if (_.isArray(responseParams)) {
                _.each(responseParams, function(param) {
                    if (_.isString(param)) {
                        responseObj[param] = null;
                    }
                    else if (_.isObject(param)) {
                        var firstKey = _.keys(param)[0];
                        if (firstKey) {
                            responseObj[firstKey] = param[firstKey];
                        }
                    }
                });
            }
            else if (_.isObject(responseParams)) {
                responseObj = responseParams;
            }

            return responseObj;
        },

        _buildGraphQLFetchQuery: function(metaData, responseParams) {
            var entity = this.resource.entityName;

            var requestObj = this._prepareRequestObj(metaData);
            var request = this._stringifyValueByGraphQL(requestObj, {objWrapperLeft:"(", objWrapperRight:")"});

            var responseObj = this._prepareResponseParams(responseParams);
            var response = this._stringifyValueByGraphQL(responseObj, {objKeyValueSeparator:""});

            var query = "{" + entity + " " + request + " " + response + "}";
            return query;
        },

        fetch: function(options) {
            options = options || {};

            var metaData = _.cloneDeep(this.metaData);
            var responseParams = options.responseParams || this.responseParams || {};
            var gqlQuery = this._buildGraphQLFetchQuery(metaData, responseParams);
            var data = {query:gqlQuery};

            var success = options.success;
            options.success = _.bind(function(collection, resp, fetchOptions) {
                var headerTotalItems = fetchOptions.xhr.getResponseHeader(TOTAL_ITEMS_HEADER);
                metaData.totalItems = parseInt(headerTotalItems, 10);

                this.lastSuccessMetaData = metaData; //we save the meta data from the context of the fetch. this is the last meta data we successfully fetched.

                if (_.isFunction(success)) {
                    success(collection, resp, fetchOptions);
                }
            }, this);

            options.type = "POST"; // graphQL works only with POST (because of its query string)
            options.data = data;
            options.contentType = "application/json";
            options.reset = true;
            options.isLoading = true; // because this fetch is with POST, BaseCollection might ignore the "Loading" event. We need to manually tell him that although it's a POST we are Loading something.

            return RevisionedCollection.prototype.fetch.call(this, options);
        },

        // we need to implement this because the BaseCollection is not trigering the fetch state if the request is not GET
        fetchError: function() {
            this.setFetchState(this.FetchStateEnum.ERROR);
        },

        parse: function(resp, options) {
            if (resp && resp.data && resp.data[this.resource.entityName]) {
                resp = resp.data[this.resource.entityName];
            }

            return RevisionedCollection.prototype.parse.call(this, resp, options);
        },

        _isFilterIgnored: function(filterName) {
            return (this.internalFilters && _.contains(this.internalFilters, filterName));
        }
    });

    GraphQLCollection.ORDER_DIRECTION = ORDER_DIRECTION;

    return GraphQLCollection;
});
